import React, { useEffect, useMemo, useState } from "react";
import { FiSave, FiX } from "react-icons/fi";

import API from "../../../../../../utils/apiCalling";
import { config } from "../../../../../../utils/apiUrl";
import useSwal from "../../../../../../hooks/useSwal";

const initialState = {
  customerName: "",
  mobile: "",
  email: "",
  companyName: "",
  address: "",
  status: "Active",
};

const AddEditCustomer = ({
  open,
  customer,
  onClose,
  onSuccess,
}) => {
  const api = useMemo(() => new API(), []);
  const swal = useSwal();

  const [form, setForm] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const isEdit = Boolean(customer?._id);

  useEffect(() => {
    if (!open) return;

    if (customer) {
      setForm({
        customerName: customer.customerName || "",
        mobile: customer.mobile || "",
        email: customer.email || "",
        companyName: customer.companyName || "",
        address: customer.address || "",
        status: customer.status || "Active",
      });
    } else {
      setForm(initialState);
    }

    setErrors({});
  }, [open, customer]);

  if (!open) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]:
        name === "mobile"
          ? value.replace(/\D/g, "").slice(0, 10)
          : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validate = () => {
    const validationErrors = {};

    if (!form.customerName.trim()) {
      validationErrors.customerName =
        "Customer name is required";
    }

    if (!form.mobile.trim()) {
      validationErrors.mobile =
        "Mobile number is required";
    } else if (!/^[6-9]\d{9}$/.test(form.mobile)) {
      validationErrors.mobile =
        "Enter a valid mobile number";
    }

    if (
      form.email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)
    ) {
      validationErrors.email =
        "Enter valid email";
    }

    setErrors(validationErrors);

    return Object.keys(validationErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      setLoading(true);

      const payload = {
        ...form,
      };

      let response;

      if (isEdit) {
        payload.id = customer._id;

        response = await api.post(
          config.updateCustomerById,
          payload
        );
      } else {
        response = await api.post(
          config.createCustomer,
          payload
        );
      }

      if (response?.status === "success") {
        await swal.success({
          icon: "success",
          title: isEdit
            ? "Customer Updated"
            : "Customer Added",
          text: response.message,
          timer: 1400,
          showConfirmButton: false,
        });

        onSuccess(response.data);
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      swal.error({
        icon: "error",
        title: "Error",
        text:
          error?.response?.data?.message ||
          error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="customer-dialog-overlay">
      <div className="customer-dialog">

        <div className="customer-dialog-header">

          <div>

            <span>
              CUSTOMER MANAGEMENT
            </span>

            <h2>
              {isEdit
                ? "Edit Customer"
                : "Add Customer"}
            </h2>

          </div>

          <button
            className="customer-dialog-close"
            onClick={onClose}
          >
            <FiX />
          </button>

        </div>

        <form onSubmit={handleSubmit}>

          <div className="customer-dialog-body">

            <div className="customer-dialog-grid">

              <div className="customer-dialog-field">

                <label>
                  Customer Name *
                </label>

                <input
                  name="customerName"
                  value={form.customerName}
                  onChange={handleChange}
                />

                <span className="customer-dialog-field-error">
                  {errors.customerName}
                </span>

              </div>

              <div className="customer-dialog-field">

                <label>
                  Mobile Number *
                </label>

                <input
                  name="mobile"
                  value={form.mobile}
                  onChange={handleChange}
                />

                <span className="customer-dialog-field-error">
                  {errors.mobile}
                </span>

              </div>

              <div className="customer-dialog-field">

                <label>
                  Email
                </label>

                <input
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                />

                <span className="customer-dialog-field-error">
                  {errors.email}
                </span>

              </div>

              <div className="customer-dialog-field">

                <label>
                  Company Name
                </label>

                <input
                  name="companyName"
                  value={form.companyName}
                  onChange={handleChange}
                />

              </div>

              {isEdit && (
                <div className="customer-dialog-field">

                  <label>
                    Status
                  </label>

                  <select
                    name="status"
                    value={form.status}
                    onChange={handleChange}
                  >
                    <option value="Active">
                      Active
                    </option>

                    <option value="Inactive">
                      Inactive
                    </option>

                    <option value="Blocked">
                      Blocked
                    </option>

                  </select>

                </div>
              )}

            </div>

            <div className="customer-dialog-field">

              <label>
                Address
              </label>

              <textarea
                rows={4}
                name="address"
                value={form.address}
                onChange={handleChange}
              />

            </div>

          </div>

          <div className="customer-dialog-footer">

            <button
              type="button"
              className="customer-dialog-cancel"
              onClick={onClose}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="customer-dialog-save"
              disabled={loading}
            >
              <FiSave />

              {loading
                ? "Saving..."
                : isEdit
                ? "Update Customer"
                : "Save Customer"}

            </button>

          </div>

        </form>

      </div>
    </div>
  );
};

export default AddEditCustomer;